import React, { useRef, Suspense, useMemo } from "react";
import { useObjectStore } from "../zustand/objects";
import { PreviewModel, ShapeTexture, PreviewAIModel, PreviewUploadedModel, UIModelTexture } from "./PreviewModel";
import PreviewText from "./PreviewText";
import PreviewImageObject from "./PreviewImageObject";
import {PreviewFigmaObject} from "./FigmaObject";
import { Camera } from "./Camera";
import { AtmosphereSky } from "./AtmosphereSky";
import { AnimatedSceneBackground } from "./AnimatedSceneBackground";
import { ViewportSunLights } from "./ViewportSunLights";
import { AnimatedFloorMaterial } from "./AnimatedFloorMaterial";
import { AnimatedGridHelper } from "./AnimatedGridHelper";
import Cursor from "./Cursor";
import PlayerFigure from "./PlayerFigure";
import { CaptureCamera } from "./CaptureHeadset";
import "../dot.css";

import { isAndroid } from 'react-device-detect';
import { Box, Text } from "@mantine/core";
import { Canvas, useThree } from "@react-three/fiber";
import {
  MBX_DARK_BACKGROUND_FILL,
  previewCanvasFrameStyle,
  previewCanvasInsetStyle,
  previewCanvasWebGlStyle,
} from "../styleFile";
import { OrbitControls } from "@react-three/drei";
import { DesktopFloorTeleport, ChangeSceneDesktop } from "./TeleportFloorGrid";
import { XR, Controllers, Interactive } from "@react-three/xr";
import { XrInvalidateWhilePresenting } from "./XrInvalidateWhilePresenting";
import PreviewPresenceLighting from "./PreviewPresenceLighting";
import { PreviewR3fInvalidateOnShapeHover } from "./PreviewR3fInvalidateOnShapeHover";
import {
  VIEWPORT_SHADOW_MAP,
  getEditorGridLineColors,
  getViewportSceneTheme,
  getViewportCappedDpr,
} from "../utils/viewportCanvasDefaults";

// softShadows();

/** Damped OrbitControls need invalidate() each change when Canvas uses frameloop="demand". */
function OrbitControlsDemand({ controlsRef, ...props }) {
  const { invalidate } = useThree();
  return (
    <OrbitControls
      ref={controlsRef}
      {...props}
      onChange={() => invalidate()}
    />
  );
}

const PREVIEW_PRESENCE_COLORS = [
  "#E57373",
  "#9575CD",
  "#4FC3F7",
  "#81C784",
  "#FFF176",
  "#FF8A65",
  "#F06292",
  "#7986CB",
];

function ThirdPersonCursorsAndHeadsets({
  presence,
  currentObjectArtboard,
  projectKey,
}) {
  const localSocketId = useObjectStore((s) => s.realtimeClientSocketId);
  const rowForProject = (row) =>
    row && (projectKey == null || String(row.projectKey) === String(projectKey));
  const colorAt = (socketKey) => {
    const m = socketKey.match(/\d+/);
    const n = m ? Number(m[0]) : 0;
    return PREVIEW_PRESENCE_COLORS[n % PREVIEW_PRESENCE_COLORS.length];
  };

  const socketIds = Object.keys(presence ?? {});
  const cursorIds = socketIds.filter(
    (mapped) =>
      rowForProject(presence[mapped]) &&
      presence[mapped].update?.cursor &&
      presence[mapped].update.cursor.page === "editor" &&
      String(presence[mapped].update.cursor.artboard) ===
        String(currentObjectArtboard)
  );
  const previewFigureIds = socketIds.filter(
    (mapped) =>
      rowForProject(presence[mapped]) &&
      presence[mapped].update?.preview &&
      presence[mapped].update.preview.page === "preview" &&
      String(presence[mapped].update.preview.artboard) ===
        String(currentObjectArtboard) &&
      mapped !== localSocketId
  );

  return (
    <>
      <Suspense fallback={null}>
        {cursorIds.map((mapped) => (
          <Cursor
            key={mapped}
            rotation={presence[mapped].update.cursor.rotation}
            position={presence[mapped].update.cursor.position}
            color={colorAt(mapped)}
            mouse={presence[mapped].update.cursor.mouse}
          />
        ))}
      </Suspense>
      <Suspense fallback={null}>
        {previewFigureIds.map((mapped) => (
          <PlayerFigure
            key={mapped}
            rotation={presence[mapped].update.preview.rotation}
            position={presence[mapped].update.preview.position}
            color={colorAt(mapped)}
            device={presence[mapped].update.preview.device}
          />
        ))}
      </Suspense>
    </>
  );
}

const ThirdPersonPreviewViewport = ({guestMode, projectKey}) => {
  const {
    objectsAreLoaded,
    floorColour,
    updateArtboard,
    viewportThemeOverride,
    artboards,
    currentObjectArtboard,
    presence,
  } = useObjectStore();

  const orbitControls = useRef();
  const cappedDpr = getViewportCappedDpr();
  const sceneTheme = getViewportSceneTheme(viewportThemeOverride);
  const backgroundFill = viewportThemeOverride === "dark" ? MBX_DARK_BACKGROUND_FILL : "#FFFFFF";
  const editorGridColors = useMemo(
    () => getEditorGridLineColors(floorColour, sceneTheme.mode),
    [floorColour, sceneTheme.mode]
  );

  const clickedShape = (dest) => {
    dest && updateArtboard(dest)
  };
  // - - - -  cursors end

  if (!objectsAreLoaded) {
    return (
      <Box
        style={{
          ...previewCanvasInsetStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--mbx-background-fill)",
          zIndex: 1,
        }}
      >
        <Text size="sm" c="dimmed">
          Loading scene…
        </Text>
      </Box>
    );
  }

  if (isAndroid) {
    return (
      <Suspense fallback={<div>Loading... </div>}>
        <div style={{ ...previewCanvasFrameStyle, backgroundColor: backgroundFill }}>
          <Canvas
            gl={{ antialias: true }}
            dpr={cappedDpr}
            style={previewCanvasWebGlStyle}
            frameloop="demand"
            onCreated={(state) => state.invalidate()}
            onPointerMissed={() => useObjectStore.getState().shapeHoverChange(false)}
          >
            <PreviewR3fInvalidateOnShapeHover />
            <XR>
              <XrInvalidateWhilePresenting />
              <Controllers />
              <AnimatedSceneBackground color={sceneTheme.background} />
              <AtmosphereSky {...sceneTheme.skyAtmosphere} />
              <ViewportSunLights
                ambientIntensity={sceneTheme.ambientIntensity}
                directionalIntensity={sceneTheme.directionalIntensity}
                directionalColor={sceneTheme.directionalColor}
                shadowMapSize={VIEWPORT_SHADOW_MAP}
              />
              <CaptureCamera
                projectKey={projectKey}
                currentObjectArtboard={currentObjectArtboard}
              />
              <PreviewPresenceLighting
                intensity={sceneTheme.previewPresenceDirectionalIntensity}
                color={sceneTheme.previewPresenceDirectionalColor}
              />
              <group>
                {Array.isArray(artboards?.[currentObjectArtboard]) && artboards[currentObjectArtboard].map((mapped, i) => (
                  mapped.category !== "text" ?
                    (mapped.object === "camera") ?
                      <Camera
                        key={mapped.id}
                        position={mapped.position}
                        rotation={mapped.rotation}
                        scale={mapped.scale} 
                        id={mapped.id}
                        object={mapped.object}
                        artboard={artboards[currentObjectArtboard]}
                        currentObjectArtboard={currentObjectArtboard}
                        objectsAreLoaded={objectsAreLoaded}
                        matrixState={mapped.matrixState}
                      /> :
                      (mapped.category === "image") ?
                        <Interactive onSelect={() => clickedShape(mapped.destination)}>
                          <PreviewImageObject
                            key={i}
                            position={mapped.position}
                            rotation={mapped.rotation}
                            scale={mapped.scale}
                            id={mapped.id}
                            destination={mapped.destination}
                            object={mapped.object}
                            category={mapped.category}
                            currentObjectArtboard={currentObjectArtboard}
                            objectsAreLoaded={objectsAreLoaded}
                            colour={mapped.colour}
                            pose={mapped.pose}
                            image={mapped.image}
                            imageSize={mapped.imageSize}
                            matrixState={mapped.matrixState}
                          />  </Interactive> :
                        (mapped.category === "figma") ?
                        <Interactive onSelect={() => clickedShape(mapped.destination)}>
                          <PreviewFigmaObject
                            key={i}
                            position={mapped.position}
                            rotation={mapped.rotation}
                            scale={mapped.scale}
                            id={mapped.id}
                            destination={mapped.destination}
                            object={mapped.object}
                            category={mapped.category}
                            currentObjectArtboard={currentObjectArtboard}
                            objectsAreLoaded={objectsAreLoaded}
                            colour={mapped.colour}
                            figmaID={mapped.figmaID}
                            figmaURL={mapped.figmaURL}
                            figmaSize={mapped.figmaSize}
                            matrixState={mapped.matrixState}
                          /> </Interactive>
                          :
                                          mapped.databaseId && (mapped.category === "ai") ?
                          <Interactive onSelect={() => clickedShape(mapped.destination)}>
                          <PreviewAIModel
                          key={mapped.id}
                          position={mapped.position}
                          rotation={mapped.rotation}
                          scale={mapped.scale}
                          id={mapped.id}
                          destination={mapped.destination}
                          object={mapped.object}
                          prompt={mapped.object}
                          category={mapped.category}
                          url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                          artboard={artboards[currentObjectArtboard]}
                          currentObjectArtboard={currentObjectArtboard}
                          objectsAreLoaded={objectsAreLoaded}
                          colour={mapped.colour}
                          pose={mapped.pose}
                          imageTexture={mapped.imageTexture}
                          isLocked={mapped.isLocked}
                          matrixState={mapped.matrixState}
                          guestMode={guestMode}
                          projectKey={projectKey}
                          databaseId={mapped.databaseId}
                          databaseURL={mapped.databaseURL}
                        /></Interactive>: (mapped.category === "uploaded") ?
                        <Suspense key={i} fallback={null}>
                        <Interactive key={i} onSelect={() => clickedShape(mapped.destination)}>
                          <PreviewUploadedModel
                        key={mapped.id}
                        position={mapped.position}
                        rotation={mapped.rotation}
                        scale={mapped.scale}
                        id={mapped.id}
                        destination={mapped.destination}
                        object={mapped.object}
                        prompt={mapped.object}
                        category={mapped.category}
                        url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                        artboard={artboards[currentObjectArtboard]}
                        currentObjectArtboard={currentObjectArtboard}
                        objectsAreLoaded={objectsAreLoaded}
                        colour={mapped.colour}
                        pose={mapped.pose}
                        imageTexture={mapped.imageTexture}
                        isLocked={mapped.isLocked}
                        matrixState={mapped.matrixState}
                        uploadedURL={mapped.uploadedURL}
                        uploadId={mapped.uploadId}
                      /></Interactive>
                      </Suspense>   :
                  mapped.imageTexture && (mapped.category === "UI") ?
                    <Suspense key={i} fallback={null}>
                      <Interactive key={i} onSelect={() => clickedShape(mapped.destination)}>
                        <UIModelTexture
                          key={mapped.id}
                          position={mapped.position}
                          rotation={mapped.rotation}
                          scale={mapped.scale}
                          id={mapped.id}
                          destination={mapped.destination}
                          object={mapped.object}
                          category={mapped.category}
                          url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                          artboard={artboards[currentObjectArtboard]}
                          currentObjectArtboard={currentObjectArtboard}
                          objectsAreLoaded={objectsAreLoaded}
                          colour={mapped.colour}
                          pose={mapped.pose}
                          imageTexture={mapped.imageTexture}
                          matrixState={mapped.matrixState}
                        />
                      </Interactive>
                    </Suspense>
                    :
                    (mapped.imageTexture && mapped.category === "shape") ?
                      <Suspense key={i} fallback={null}>
                        <Interactive key={i} onSelect={() => clickedShape(mapped.destination)}>
                          <ShapeTexture
                            key={mapped.id}
                            position={mapped.position}
                            rotation={mapped.rotation}
                            scale={mapped.scale}
                            id={mapped.id}
                            destination={mapped.destination}
                            object={mapped.object}
                            category={mapped.category}
                            url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                            artboard={artboards[currentObjectArtboard]}
                            currentObjectArtboard={currentObjectArtboard}
                            objectsAreLoaded={objectsAreLoaded}
                            colour={mapped.colour}
                            pose={mapped.pose}
                            imageTexture={mapped.imageTexture}
                            matrixState={mapped.matrixState}
                          />
                        </Interactive>
                      </Suspense>
                      :
                      <Suspense key={i} fallback={null}>
                        <Interactive key={i} onSelect={() => clickedShape(mapped.destination)}>
                          <PreviewModel
                            key={i}
                            position={mapped.position}
                            rotation={mapped.rotation}
                            scale={mapped.scale}
                            id={mapped.id}
                            destination={mapped.destination}
                            object={mapped.object}
                            category={mapped.category}
                            url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                            artboard={artboards[currentObjectArtboard]}
                            currentObjectArtboard={currentObjectArtboard}
                            objectsAreLoaded={objectsAreLoaded}
                            colour={mapped.colour}
                            pose={mapped.pose}
                            imageTexture={mapped.imageTexture}
                            matrixState={mapped.matrixState}
                            curved={mapped.curved}
                            curveAmount={mapped.curveAmount}
                            modelPath={mapped.modelPath}
                          />
                        </Interactive>
                      </Suspense>
        :
        <Suspense key={i} fallback={null}>
          <Interactive key={i} onSelect={() => clickedShape(mapped.destination)}>
            <PreviewText
              key={i}
              content={mapped.content}
              position={mapped.position}
              rotation={mapped.rotation}
              scale={mapped.scale}
              id={mapped.id}
              destination={mapped.destination}
              artboard={artboards[currentObjectArtboard]}
              currentObjectArtboard={currentObjectArtboard}
              objectsAreLoaded={objectsAreLoaded}
              colour={mapped.colour}
              bGColour={mapped.bGColour}
              matrixState={mapped.matrixState}
            />
          </Interactive>
        </Suspense>
                ))}
              </group>
             
              <mesh
                receiveShadow
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, -0.5, 0]}
              >
                <planeGeometry attach="geometry" args={[50, 50]} />
                <AnimatedFloorMaterial
                  type="basic"
                  color={floorColour}
                  opacity={1}
                />
              </mesh>
              <AnimatedGridHelper
                position={[0, -0.5, 0]}
                size={50}
                divisions={50}
                centerLineColor={editorGridColors.centerLine}
                gridColor={editorGridColors.grid}
                onUpdate={(self) => {
                  self.traverse((c) => {
                    c.userData.previewIgnoreTeleportRaycast = true;
                  });
                }}
              />
              <ThirdPersonCursorsAndHeadsets
                presence={presence}
                currentObjectArtboard={currentObjectArtboard}
                projectKey={projectKey}
              />
            </XR></Canvas>
        </div>
      </Suspense>
    );
  }

  return (
    <>
      <Suspense fallback={<div>Loading... </div>}>
        <div style={{ ...previewCanvasFrameStyle, backgroundColor: backgroundFill }}>
        <Canvas
          style={previewCanvasWebGlStyle}
          camera={{
            position: [0, 15, 15],
            fov: 25
          }}
          dpr={cappedDpr}
          frameloop="demand"
          shadows
          onCreated={(state) => state.invalidate()}
          onPointerMissed={() => useObjectStore.getState().shapeHoverChange(false)}
        >
          <PreviewR3fInvalidateOnShapeHover />
          <OrbitControlsDemand
            controlsRef={orbitControls}
            maxPolarAngle={1.5}
            dampingFactor={1}
            keyPanSpeed={15}
            zoomSpeed={0.5}
            maxDistance={50}
            minDistance={5}
            enableZoom={true}
            enableDamping={true}
          />
          <CaptureCamera
            projectKey={projectKey}
            currentObjectArtboard={currentObjectArtboard}
          />
          <AnimatedSceneBackground color={sceneTheme.background} />
          <AtmosphereSky {...sceneTheme.skyAtmosphere} />
          <ViewportSunLights
            ambientIntensity={sceneTheme.ambientIntensity}
            directionalIntensity={sceneTheme.directionalIntensity}
            directionalColor={sceneTheme.directionalColor}
            shadowMapSize={VIEWPORT_SHADOW_MAP}
          />
          <PreviewPresenceLighting
            intensity={sceneTheme.previewPresenceDirectionalIntensity}
            color={sceneTheme.previewPresenceDirectionalColor}
          />
          <group>
          {/* {artboards && <ChangePreviewScene/>} */}
            {Array.isArray(artboards?.[currentObjectArtboard]) && artboards[currentObjectArtboard].map((mapped, i) => (
              mapped.category !== "text" ?
                (mapped.object === "camera") ?'' :
                  (mapped.category === "image") ?
                  <PreviewImageObject
                  key={i}
                  position={mapped.position}
                  rotation={mapped.rotation}
                  scale={mapped.scale}
                  id={mapped.id}
                  destination={mapped.destination}
                  object={mapped.object}
                  category={mapped.category}
                  currentObjectArtboard={currentObjectArtboard}
                  objectsAreLoaded={objectsAreLoaded}
                  colour={mapped.colour}
                  pose={mapped.pose}
                  image={mapped.image}
                  imageSize={mapped.imageSize}
                  matrixState={mapped.matrixState}
                  curved={mapped.curved}
                  curveAmount={mapped.curveAmount}
                /> : (mapped.category === "figma") ?
                  <PreviewFigmaObject
                    key={i}
                    position={mapped.position}
                    rotation={mapped.rotation}
                    scale={mapped.scale}
                    id={mapped.id}
                    destination={mapped.destination}
                    object={mapped.object}
                    category={mapped.category}
                    currentObjectArtboard={currentObjectArtboard}
                    objectsAreLoaded={objectsAreLoaded}
                    colour={mapped.colour}
                    figmaID={mapped.figmaID}
                    figmaURL={mapped.figmaURL}
                    figmaSize={mapped.figmaSize}
                    matrixState={mapped.matrixState}
                    curved={mapped.curved}
                    curveAmount={mapped.curveAmount}
                  /> :
                                  mapped.databaseId && (mapped.category === "ai") ?
                    <PreviewAIModel
                      key={mapped.id}
                      position={mapped.position}
                      rotation={mapped.rotation}
                      scale={mapped.scale}
                      id={mapped.id}
                      destination={mapped.destination}
                      object={mapped.object}
                      prompt={mapped.object}
                      category={mapped.category}
                      url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                      artboard={artboards[currentObjectArtboard]}
                      currentObjectArtboard={currentObjectArtboard}
                      objectsAreLoaded={objectsAreLoaded}
                      colour={mapped.colour}
                      pose={mapped.pose}
                      imageTexture={mapped.imageTexture}
                      isLocked={mapped.isLocked}
                      matrixState={mapped.matrixState}
                      guestMode={guestMode}
                      aiKey={mapped.aiKey}
                      projectKey={projectKey}
                      databaseId={mapped.databaseId}
                      databaseURL={mapped.databaseURL}
                    /> : (mapped.category === "uploaded") ?
                      <PreviewUploadedModel
                        key={mapped.id}
                        position={mapped.position}
                        rotation={mapped.rotation}
                        scale={mapped.scale}
                        id={mapped.id}
                        destination={mapped.destination}
                        object={mapped.object}
                        prompt={mapped.object}
                        category={mapped.category}
                        url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                        artboard={artboards[currentObjectArtboard]}
                        currentObjectArtboard={currentObjectArtboard}
                        objectsAreLoaded={objectsAreLoaded}
                        colour={mapped.colour}
                        pose={mapped.pose}
                        imageTexture={mapped.imageTexture}
                        isLocked={mapped.isLocked}
                        matrixState={mapped.matrixState}
                        uploadedURL={mapped.uploadedURL}
                        uploadId={mapped.uploadId}
                      /> :
                      mapped.imageTexture && (mapped.category === "UI") ?
                        <UIModelTexture
                          key={mapped.id}
                          position={mapped.position}
                          rotation={mapped.rotation}
                          scale={mapped.scale}
                          id={mapped.id}
                          destination={mapped.destination}
                          object={mapped.object}
                          category={mapped.category}
                          url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                          artboard={artboards[currentObjectArtboard]}
                          currentObjectArtboard={currentObjectArtboard}
                          objectsAreLoaded={objectsAreLoaded}
                          colour={mapped.colour}
                          pose={mapped.pose}
                          imageTexture={mapped.imageTexture}
                          isLocked={mapped.isLocked}
                          matrixState={mapped.matrixState}
                        />
                        :
                        (mapped.imageTexture && mapped.category === "shape") ?
                          <ShapeTexture
                            key={i}
                            position={mapped.position}
                            rotation={mapped.rotation}
                            scale={mapped.scale}
                            id={mapped.id}
                            destination={mapped.destination}
                            object={mapped.object}
                            category={mapped.category}
                            url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                            artboard={artboards[currentObjectArtboard]}
                            currentObjectArtboard={currentObjectArtboard}
                            objectsAreLoaded={objectsAreLoaded}
                            colour={mapped.colour}
                            pose={mapped.pose}
                            imageTexture={mapped.imageTexture}
                            matrixState={mapped.matrixState}
                          /> :
  
                          <PreviewModel
                            key={i}
                            position={mapped.position}
                            rotation={mapped.rotation}
                            scale={mapped.scale}
                            id={mapped.id}
                            destination={mapped.destination}
                            object={mapped.object}
                            category={mapped.category}
                            url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
                            artboard={artboards[currentObjectArtboard]}
                            currentObjectArtboard={currentObjectArtboard}
                            objectsAreLoaded={objectsAreLoaded}
                            colour={mapped.colour}
                            pose={mapped.pose}
                            imageTexture={mapped.imageTexture}
                            matrixState={mapped.matrixState}
                            curved={mapped.curved}
                            curveAmount={mapped.curveAmount}
                            modelPath={mapped.modelPath}
                          /> :
            <PreviewText
              key={i}
              content={mapped.content}
              position={mapped.position}
              rotation={mapped.rotation}
              scale={mapped.scale}
              id={mapped.id}
              destination={mapped.destination}
              artboard={artboards[currentObjectArtboard]}
              currentObjectArtboard={currentObjectArtboard}
              objectsAreLoaded={objectsAreLoaded}
              colour={mapped.colour}
              bGColour={mapped.bGColour}
              matrixState={mapped.matrixState}
            />
  
            ))}
          </group>


          <mesh
            receiveShadow
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, -0.5, 0]}
            userData={{ previewTeleportFloor: true }}
          >
            <planeGeometry attach="geometry" args={[50, 50]} />
            <AnimatedFloorMaterial
              type="basic"
              color={floorColour}
              opacity={1}
            />
          </mesh>
          <DesktopFloorTeleport />
          <ChangeSceneDesktop />
          <AnimatedGridHelper
            position={[0, -0.5, 0]}
            size={50}
            divisions={50}
            centerLineColor={editorGridColors.centerLine}
            gridColor={editorGridColors.grid}
            onUpdate={(self) => {
              self.traverse((c) => {
                c.userData.previewIgnoreTeleportRaycast = true;
              });
            }}
          />
          <ThirdPersonCursorsAndHeadsets
            presence={presence}
            currentObjectArtboard={currentObjectArtboard}
            projectKey={projectKey}
          />
        </Canvas>
        </div>
      </Suspense>
    </>
  );
};

export default ThirdPersonPreviewViewport;
